import { forwardRef, useCallback } from 'react';
import cn from 'classnames';
import ReactSelect, {
  components,
  IndicatorProps,
  NamedProps,
  StylesConfig,
  OptionTypeBase,
  ValueContainerProps,
} from 'react-select';
import { isMobile } from 'react-device-detect';
import ReactAsyncSelect, { AsyncProps } from 'react-select/async';
import { AsyncPaginate } from 'react-select-async-paginate';
// Helpers
import { BaseInputProps } from 'helpers/input';
// Components
import { InputFooter } from 'ui3/InputFooter/InputFooter';
// UI
import MillionsIcon, { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './Select.module.scss';

export type InputSize = 'large' | 'medium' | 'small';

type BasicSelectProps = NamedProps<OptionTypeBase, boolean> &
  (
    | { type?: 'default'; loadOptions?: never; cacheUniqs?: never }
    | ({ type: 'async' } & AsyncProps<OptionTypeBase>)
    | ({ type: 'async-paginate' } & Record<string, unknown>)
  );

type SelectProps = {
  labelClassName?: string;
  dropdownIndicator?: IconNames | null;
  dropdownIndicatorPosition?: 'left' | 'right';
  size?: InputSize;
} & BasicSelectProps &
  BaseInputProps;

const getControlBorderColor = (
  error: boolean,
  isFocused: boolean,
  disabled: boolean
): string => {
  if (error) {
    return '#f43f36';
  }

  if (isFocused) {
    return '#d8a445'; // accent-default
  }

  if (disabled) {
    return '#666666';
  }

  return '#27282d';
};

const defaultFontStyles: React.CSSProperties = {
  fontFamily: 'Fira Sans',
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 400,
};

const getCustomStyles = (
  disabled: boolean,
  error: boolean,
  size: SelectProps['size'],
  dropdownIndicatorPosition: SelectProps['dropdownIndicatorPosition']
): StylesConfig<OptionTypeBase, boolean> => {
  const defaultPadding =
    size === 'small' ? '4px 8px' : size === 'medium' ? '5px 12px' : '9px 12px';
  return {
    container: (provided) => ({
      ...provided,
      ...defaultFontStyles,
      minWidth: 130,
      width: '100%',
      cursor: disabled ? 'not-allowed' : 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1001,
      backgroundColor: '#141519',
      border: '1px solid #27282D',
      borderRadius: 10,
      boxShadow: 'none',
      overflow: 'hidden',
    }),
    option: (provided, { data: { disabled: isOptionDisabled } }) => {
      return {
        ...provided,
        ...defaultFontStyles,
        margin: 0,
        padding: defaultPadding,
        backgroundColor: isOptionDisabled ? '#666666' : '#141519',
        textAlign: 'left',
        color: isOptionDisabled ? '#d9d9d9' : '#ffffff',
        borderRadius: 0,
        '&:hover': {
          cursor: isOptionDisabled ? 'not-allowed' : 'pointer',
          backgroundColor: isOptionDisabled ? '#666666' : '#202127',
        },
      };
    },
    singleValue: (provided) => ({
      ...provided,
      ...defaultFontStyles,
      margin: 0,
      color: disabled ? '#666666' : '#ffffff',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      margin: 'auto',
      padding: 0,
      backgroundColor: disabled ? '#202127' : '#141519',
      justifyContent: 'flex-end',
      cursor: disabled ? 'not-allowed' : 'pointer',
      svg: {
        color: '#999999',
        fontSize: 24,
      },
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    input: (provided) => ({
      ...provided,
      ...defaultFontStyles,
      color: disabled ? '#666666' : '#ffffff',
      margin: 0,
      // margin-left is not working without kebab case, but camelCase doesn't apply attribute to the element
      ...(dropdownIndicatorPosition === 'left' && { margin: '0 0 0 8px' }),
      input: {
        ...defaultFontStyles,
        color: disabled ? '#666666' : '#ffffff',
      },
    }),
    valueContainer: (provided) => {
      return {
        ...provided,
        ...defaultFontStyles,
        padding: defaultPadding,
        cursor: disabled ? 'not-allowed' : 'pointer',
      };
    },
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      borderRadius: 10,
    }),
    control: (provided, { isFocused }) => ({
      ...provided,
      minHeight: 'unset',
      boxSizing: 'border-box',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: getControlBorderColor(error, isFocused, disabled),
      borderRadius: 5,
      backgroundColor: disabled ? '#202127' : '#141519',
      boxShadow: 'none',
      overflow: 'hidden',
      cursor: disabled ? 'not-allowed' : 'pointer',
      ':hover': {
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderColor: '#525254',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      ...defaultFontStyles,
      margin: 0,
      color: '#999999',
      // margin-left is not working without kebab case, but camelCase doesn't apply attribute to the element
      ...(dropdownIndicatorPosition === 'left' && { margin: '0 0 0 32px' }),
    }),
  };
};

const Select = forwardRef(
  (
    {
      type = 'default',
      error,
      label,
      labelClassName,
      className,
      inputId,
      dropdownIndicator,
      dropdownIndicatorPosition = 'right',
      size = 'large',
      components: customComponents,
      styles: customStyles,
      hint,
      notice,
      ...rest
    }: SelectProps,
    ref
  ) => {
    const isDefaultType = type === 'default';
    const isAsyncType = type === 'async';
    const isAsyncPaginateType = type === 'async-paginate';
    const isError = Boolean(error);
    const isDisabled = !!rest.disabled || !!rest.isDisabled;
    const tooltipId = `select-tooltip-${inputId || 'select'}`;

    const DropdownIndicator = useCallback(
      (props: IndicatorProps<OptionTypeBase, boolean>) => {
        if (
          dropdownIndicator === null ||
          dropdownIndicatorPosition === 'left'
        ) {
          return null;
        }

        return (
          <>
            <components.DropdownIndicator {...props}>
              <MillionsIcon name={dropdownIndicator || 'chevronDown'} />
            </components.DropdownIndicator>
          </>
        );
      },
      [dropdownIndicator, dropdownIndicatorPosition]
    );

    const ValueContainer = useCallback(
      (props: ValueContainerProps<OptionTypeBase, boolean>) => {
        const { children } = props;
        return (
          <components.ValueContainer {...props}>
            {dropdownIndicatorPosition === 'left' && (
              <MillionsIcon
                name={dropdownIndicator || 'chevronDown'}
                className={styles.leftIcon}
              />
            )}
            {children}
          </components.ValueContainer>
        );
      },
      [dropdownIndicatorPosition, dropdownIndicator]
    );

    const commonProps = {
      inputId,
      isDisabled,
      styles: {
        ...getCustomStyles(
          isDisabled,
          isError,
          size,
          dropdownIndicatorPosition
        ),
        ...customStyles,
      },
      isOptionDisabled: (option: any) => !!option.disabled,
      components: { ValueContainer, DropdownIndicator, ...customComponents },
      inputRef: ref,
      menuShouldBlockScroll: isMobile,
      captureMenuScroll: false,
      ...rest,
    };

    return (
      <div className={cn(styles.root, className)}>
        {label && (
          <label htmlFor={inputId} className={cn(styles.label, labelClassName)}>
            {label}
          </label>
        )}

        {isDefaultType && <ReactSelect {...commonProps} />}

        {isAsyncType && <ReactAsyncSelect {...(commonProps as any)} />}

        {isAsyncPaginateType && <AsyncPaginate {...commonProps} />}

        <InputFooter
          hint={hint}
          notice={notice}
          error={error}
          tooltipId={tooltipId}
        />
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
